import { enableProdMode, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from "@angular/core";
import { environment } from "./environments/environment";
import { bootstrapApplication } from "@angular/platform-browser";
import { AppComponent } from "./app/app.component";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { OAuthModule, OAuthService, OAuthStorage } from "angular-oauth2-oidc";
import { GoogleMapsModule } from "@angular/google-maps";
import { JwtInterceptor } from "./app/authentication/interceptor/jwt.interceptor";
import { RfAuthenticationService } from "./app/authentication/rf-authentication.service";
import { ErrorInterceptor } from "./app/shared/interceptor/error-interceptor/error.interceptor";
import { HttpALDInterceptor } from "./app/shared/interceptor/http-interceptor/http.interceptor";
import { LanguageService } from "./app/shared/service/language-service/language.service";
import { MatDialogModule } from "@angular/material/dialog";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from "@angular/material/button-toggle";
import { MaterialModule } from "./app/shared/module/material.module";
import { provideRouter, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { OVERLAY_DEFAULT_CONFIG } from "@angular/cdk/overlay";
import { routes } from "./app/app-routing.module";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { registerLocaleData } from "@angular/common";
import localeIt from '@angular/common/locales/it';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from "@angular/fire/messaging";
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

// import "hammerjs";

declare var initMobileApp: Function;
registerLocaleData(localeIt)

if (environment.production) {
  enableProdMode();
}

function cleanHashRedirect(): void {
  const hash = window.location.hash;

  if (
    hash && (
      hash.startsWith('#/support/webform') ||
      hash.startsWith('#/support/webform-reclami') ||
      hash.startsWith('#/maintenance-appointments/service-centers')
    )
  ) {
    const newPath = hash.substring(1);
    window.history.replaceState(null, '', newPath);
  }
}

cleanHashRedirect();

if (!environment.mobile) {
  bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      provideAnimations(),
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      // Hide the Material button-toggle selection indicator (checkmark) so filter
      // selects don't show the transient blue tick and don't shift the label right when selected.
      { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true } },
      provideRouter(routes, withInMemoryScrolling({
        scrollPositionRestoration: 'top'
      }), withRouterConfig({ onSameUrlNavigation: 'reload' })),
      provideHttpClient(withInterceptorsFromDi()),
      provideCharts(withDefaultRegisterables()),
      provideFirebaseApp(() => initializeApp(environment.firebase)),
      provideMessaging(() => getMessaging()),
      OAuthService,
      { provide: OAuthStorage, useValue: window.localStorage },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpALDInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: JwtInterceptor,
        multi: true,
      },
      { provide: LOCALE_ID, useValue: 'it-IT' },
      {
        provide: LOCALE_ID,
        deps: [LanguageService],
        useFactory: (languageService: LanguageService) => {
          return languageService.getUsersLocale();
        },
      },
      importProvidersFrom(
        MaterialModule,
        MatDialogModule,
        OAuthModule.forRoot(),
        GoogleMapsModule,
        MatSnackBarModule
      )
    ]
  })
    .catch(err => console.error(err));
} else {
  // Cordova Mobile App
  let script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = "cordova.js";
  script.onload = () => {
    document.addEventListener("deviceready", () => {
      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = "assets/js/mobile.js";
      script.onload = () => {
        bootstrapApplication(AppComponent, {
          providers: [
            provideZoneChangeDetection(),
            provideAnimations(),
            { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
            // Hide the Material button-toggle selection indicator (checkmark) so filter
            // selects don't show the transient blue tick and don't shift the label right when selected.
            { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true } },
            provideRouter(routes, withInMemoryScrolling({
              scrollPositionRestoration: 'top'
            }), withRouterConfig({ onSameUrlNavigation: 'reload' })),
            provideHttpClient(withInterceptorsFromDi()),
            provideCharts(withDefaultRegisterables()),
            provideFirebaseApp(() => initializeApp(environment.firebase)),
            provideMessaging(() => getMessaging()),
            OAuthService,
            { provide: OAuthStorage, useValue: window.localStorage },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: HttpALDInterceptor,
              multi: true,
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: ErrorInterceptor,
              multi: true,
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: JwtInterceptor,
              multi: true,
              deps: [RfAuthenticationService],
            },
            { provide: LOCALE_ID, useValue: 'it-IT' },
            {
              provide: LOCALE_ID,
              deps: [LanguageService],
              useFactory: (languageService: LanguageService) => {
                return languageService.getUsersLocale();
              },
            },
            importProvidersFrom(
              MaterialModule,
              MatDialogModule,
              OAuthModule.forRoot(),
              GoogleMapsModule,
              MatSnackBarModule
            )
          ]
        })
          .then(() => initMobileApp())
          .catch(err => console.error(err));
      };
      document.getElementsByTagName('head')[0].appendChild(script);
    }, false);
  };

  document.getElementsByTagName('head')[0].appendChild(script);
}

